import { FieldValues } from "react-hook-form";

import { get, omit } from "lodash";

import { ACCESSORIES_SUB_TYPES } from "constants/products";
import { WarrantySpecification } from "services/types/master-products.type";
import { Product, ProductCatalogOption } from "services/types/products.type";
import { normalizeFloatingNumberCorrection } from "utils/common";

import {
  ACCESSORIES_STEP,
  BINAURAL_DOME_SUBSTEP,
  BINAURAL_EARMOLD_SUBSTEP,
  BINAURAL_HEARING_AID_SUBSTEP,
  BINAURAL_RECEIVER_SUBSTEP,
  BINAURAL_TUBE_SUBSTEP,
  CONFIGURATION_STEP,
  HEARING_AIDS_STEP,
  LEFT_DOME_SUBSTEP,
  LEFT_EARMOLD_SUBSTEP,
  LEFT_HEARING_AID_SUBSTEP,
  LEFT_RECEIVER_SUBSTEP,
  LEFT_TUBE_SUBSTEP,
  RIGHT_DOME_SUBSTEP,
  RIGHT_EARMOLD_SUBSTEP,
  RIGHT_HEARING_AID_SUBSTEP,
  RIGHT_RECEIVER_SUBSTEP,
  RIGHT_TUBE_SUBSTEP,
  WARRANTIES_STEP
} from "../../constants";
import { AccessoriesStepValues } from "../../steps/accessories-step/accessories-step-values.type";
import { DomeValues } from "../../steps/hearing-aids-step/substeps/domes/dome-values.type";
import { EarmoldValues } from "../../steps/hearing-aids-step/substeps/earmolds/earmold-values.type";
import { HearingAidValues } from "../../steps/hearing-aids-step/substeps/hearing-aids/hearing-aid-values.type";
import { ReceiverValues } from "../../steps/hearing-aids-step/substeps/receivers/receiver-values.type";
import { TubeValues } from "../../steps/hearing-aids-step/substeps/tubes/tube-values.type";
import {
  EARS_CONFIGURATION_LEFT,
  EARS_CONFIGURATION_RIGHT,
  OrderConfigurationStepValues
} from "../../steps/order-configuration-step/order-configuration-step-values.type";
import { WarrantiesStepValues } from "../../steps/warranties-step/warranties-step-values.type";
import { CartItem, StepId, StepsDataState } from "../hearing-aids-wizard.type";

export const stepHasData = (data?: FieldValues) => {
  if (!data) return false;

  return Boolean(Object.keys(data).filter((key) => Boolean(data[key])).length);
};

export const joinValues = (values: Array<string | null | undefined>) => {
  return values.filter((value) => Boolean(value?.toString().trim())).join(" ");
};

export const getDiscountPercent = (
  actual_price: number,
  original_price: number,
  quantity: number = 1
) => {
  if (actual_price >= original_price * quantity) return 0;

  return normalizeFloatingNumberCorrection(
    100 - (actual_price / (original_price * quantity)) * 100
  );
};

const getEarStepId = (stepsData: StepsDataState, earStepIds: StepId[]) => {
  const ear = (
    (stepsData[CONFIGURATION_STEP] || {}) as OrderConfigurationStepValues
  ).ears;

  if (ear === EARS_CONFIGURATION_LEFT) return earStepIds[0];
  if (ear === EARS_CONFIGURATION_RIGHT) return earStepIds[1];
  return earStepIds[2];
};

export function getHearingAidItem(
  stepsData: StepsDataState,
  ear: "left" | "right"
): CartItem | undefined {
  const stepId: StepId = getEarStepId(stepsData, [
    LEFT_HEARING_AID_SUBSTEP,
    RIGHT_HEARING_AID_SUBSTEP,
    BINAURAL_HEARING_AID_SUBSTEP
  ]);

  if (!get(stepsData, `${stepId}.${ear}`)) return undefined;

  const data = stepsData[stepId][ear] as HearingAidValues;
  if (!data?.hearing_aid) return undefined;

  const { hearing_aid, hearing_aid_from_stock } = data;
  const actual_price = Number(data.actual_price);

  const discount_percent = getDiscountPercent(
    actual_price,
    Number(data.original_price_with_components)
  );
  const serial_number = hearing_aid_from_stock?.serial_number;

  return {
    ear,
    header: "Hearing Aid",
    label: joinValues([
      hearing_aid?.display_name || "",
      serial_number ? `- S/N: ${serial_number}` : ""
    ]),
    isParent: true,
    stepId,
    parentStepId: HEARING_AIDS_STEP,
    product: {
      productFieldName: "hearing_aid",
      // Cast: the step form types its pick narrowly (ENG-2561); the picker stored the whole product.
      product: hearing_aid as ProductCatalogOption,
      quantity: 1,
      discount_percent,
      actual_price,
      original_price: Number(data.hearing_aid?.price || "0"),
      original_price_with_components: Number(
        data.original_price_with_components
      )
    },
    sum: actual_price
  };
}
export function getReceiverItem(
  stepsData: StepsDataState,
  ear: "left" | "right"
): CartItem | undefined {
  const stepId: StepId = getEarStepId(stepsData, [
    LEFT_RECEIVER_SUBSTEP,
    RIGHT_RECEIVER_SUBSTEP,
    BINAURAL_RECEIVER_SUBSTEP
  ]);

  if (!get(stepsData, `${stepId}.${ear}`)) return undefined;

  const data = stepsData[stepId][ear] as ReceiverValues;
  if (!data?.receiver) return undefined;

  const { receiver, receiver_from_stock, gain, length } = data;
  const { display_name = "" } = receiver || {};
  const actual_price = Number(data.actual_price || "0");
  const original_price = Number(receiver?.component_price || "0");
  const serial_number = receiver_from_stock?.serial_number;

  return {
    ear,
    header: "Receiver",
    label: joinValues([
      display_name,
      gain,
      length,
      serial_number ? `- S/N: ${serial_number}` : ""
    ]),
    isParent: false,
    product: {
      productFieldName: "receiver",
      // Cast: the step form types its pick narrowly (ENG-2561); the picker stored the whole product.
      product: receiver as ProductCatalogOption,
      quantity: 1,
      discount_percent: getDiscountPercent(actual_price, original_price),
      actual_price,
      original_price
    },
    stepId,
    parentStepId: HEARING_AIDS_STEP,
    sum: actual_price
  };
}

export function getEarmoldItem(
  stepsData: StepsDataState,
  ear: "left" | "right"
): CartItem | undefined {
  const stepId: StepId = getEarStepId(stepsData, [
    LEFT_EARMOLD_SUBSTEP,
    RIGHT_EARMOLD_SUBSTEP,
    BINAURAL_EARMOLD_SUBSTEP
  ]);
  if (!get(stepsData, `${stepId}.${ear}`)) return undefined;

  const data = stepsData[stepId][ear] as EarmoldValues;
  if (!data?.earmold) return undefined;

  const { earmold, earmold_from_stock, canal_length, venting } = data;
  const type = earmold?.display_name;
  const actual_price = Number(data?.actual_price || "0");

  const original_price = Number(earmold?.price || "0");
  const serial_number = earmold_from_stock?.serial_number;

  return {
    ear,
    header: "Earmold",
    label: joinValues([
      type,
      canal_length,
      venting,
      serial_number ? `- S/N: ${serial_number}` : ""
    ]),
    isParent: true,
    product: {
      productFieldName: "earmold",
      // Cast: the step form types its pick narrowly (ENG-2561); the picker stored the whole product.
      product: earmold as ProductCatalogOption,
      quantity: 1,
      discount_percent: getDiscountPercent(actual_price, original_price),
      actual_price,
      original_price
    },
    stepId,
    parentStepId: HEARING_AIDS_STEP,
    sum: actual_price
  };
}

export function getDomeItem(
  stepsData: StepsDataState,
  ear: "left" | "right"
): CartItem | undefined {
  const stepId: StepId = getEarStepId(stepsData, [
    LEFT_DOME_SUBSTEP,
    RIGHT_DOME_SUBSTEP,
    BINAURAL_DOME_SUBSTEP
  ]);

  if (!get(stepsData, `${stepId}.${ear}`)) return undefined;

  const data = stepsData[stepId][ear] as DomeValues;
  if (!data?.dome) return undefined;

  const {
    dome,
    dome_from_stock,
    type: dome_type = "",
    size: dome_size = ""
  } = data;

  const actual_price = Number(data.actual_price || "0");
  const original_price = Number(dome.component_price || "0");
  const serial_number = dome_from_stock?.serial_number;

  return {
    ear,
    header: "Dome",
    label: joinValues([
      dome?.display_name,
      dome_type,
      dome_size,
      serial_number ? `- S/N: ${serial_number}` : ""
    ]),
    isParent: false,
    product: {
      productFieldName: "dome",
      // Cast: the step form types its pick narrowly (ENG-2561); the picker stored the whole product.
      product: dome as ProductCatalogOption,
      quantity: 1,
      discount_percent: getDiscountPercent(actual_price, original_price),
      actual_price,
      original_price
    },
    stepId,
    parentStepId: HEARING_AIDS_STEP,
    sum: actual_price
  };
}

export function getTubeItem(
  stepsData: StepsDataState,
  ear: "left" | "right"
): CartItem | undefined {
  const stepId: StepId = getEarStepId(stepsData, [
    LEFT_TUBE_SUBSTEP,
    RIGHT_TUBE_SUBSTEP,
    BINAURAL_TUBE_SUBSTEP
  ]);

  if (!get(stepsData, `${stepId}.${ear}`)) return undefined;

  const data = stepsData[stepId][ear] as TubeValues;
  if (!data?.tube) return undefined;

  const { tube, tube_from_stock, size: tube_size = "" } = data;

  const actual_price = Number(data.actual_price || "0");
  const original_price = Number(tube.component_price || "0");
  const serial_number = tube_from_stock?.serial_number;

  return {
    ear,
    header: "Tube",
    label: joinValues([
      tube?.display_name,
      tube_size,
      serial_number ? `- S/N: ${serial_number}` : ""
    ]),
    isParent: false,
    product: {
      productFieldName: "tube",
      // Cast: the step form types its pick narrowly (ENG-2561); the picker stored the whole product.
      product: tube as ProductCatalogOption,
      quantity: 1,
      discount_percent: getDiscountPercent(actual_price, original_price),
      actual_price,
      original_price
    },
    stepId,
    parentStepId: HEARING_AIDS_STEP,
    sum: actual_price
  };
}

export function getAccessoriesItems(stepsData: StepsDataState): CartItem[] {
  const data = stepsData[ACCESSORIES_STEP] as AccessoriesStepValues;

  if (!data?.products?.length) return [];

  return data.products.map((product, index) => ({
    header: "Accessories",
    label: joinValues([
      ACCESSORIES_SUB_TYPES.getFromCode(product?.master_product?.subtype),
      product.display_name
    ]),
    isParent: true,
    product: {
      productFieldName: `products[${index}]`,
      product: omit(product, ["quantity", "actual_price"]) as Product,
      quantity: Number(product.quantity),
      discount_percent: getDiscountPercent(
        Number(product.actual_price || "0"),
        Number(product.component_price || "0"),
        Number(product.quantity)
      ),
      actual_price: Number(product.actual_price || "0"),
      original_price: Number(
        product.use_component_price
          ? product.component_price || "0"
          : product?.price || "0"
      )
    },
    useComponentPrice: product.use_component_price,
    stepId: ACCESSORIES_STEP,
    sum: Number(product.actual_price) * Number(product.quantity)
  }));
}

export function getManufacturerWarrantyItem(
  stepsData: StepsDataState
): CartItem | undefined {
  const data = stepsData[WARRANTIES_STEP] as WarrantiesStepValues;

  if (!data || !data.manufacturer_warranty) return undefined;

  const warranty = data?.manufacturer_warranty?.master_product || {};

  const specification = warranty.specification as WarrantySpecification;

  const actual_price = Number(data.manufacturer_warranty.actual_price || "0");
  const original_price = Number(
    data.manufacturer_warranty.component_price || "0"
  );

  return {
    header: "Additional MFR Repair Warranty",
    label: joinValues([
      data?.manufacturer_warranty?.display_name,
      specification?.warranty_months
        ? `Months: ${specification?.warranty_months}`
        : ""
    ]),
    isParent: false,
    stepId: WARRANTIES_STEP,
    product: {
      productFieldName: "manufacturer_warranty",
      product: data.manufacturer_warranty,
      quantity: 1,
      discount_percent: getDiscountPercent(actual_price, original_price),
      actual_price,
      original_price
    },
    sum: actual_price
  };
}

export function getLndWarrantyItem(
  stepsData: StepsDataState
): CartItem | undefined {
  const data = stepsData[WARRANTIES_STEP] as WarrantiesStepValues;

  if (!data || !data.lnd_warranty) return undefined;

  const warranty = data?.lnd_warranty?.master_product || {};

  const specification = warranty.specification as WarrantySpecification;

  const actual_price = Number(data.lnd_warranty.actual_price || "0");
  const original_price = Number(data.lnd_warranty.component_price || "0");

  return {
    header: "Additional L&D Warranty",
    label: joinValues([
      data?.lnd_warranty?.display_name,
      specification?.warranty_months
        ? `Months: ${specification?.warranty_months}`
        : ""
    ]),
    isParent: false,
    stepId: WARRANTIES_STEP,
    product: {
      productFieldName: "lnd_warranty",
      product: data.lnd_warranty,
      quantity: 1,
      discount_percent: getDiscountPercent(actual_price, original_price),
      actual_price,
      original_price
    },
    sum: actual_price
  };
}

export const getCartItemGroupActualPrices = (items: CartItem[]) =>
  [...items.map((item) => String(item.product.actual_price)), "0", "0"].filter(
    (_, index) => index < 2
  );
